import { serverResponse } from "../util/fakeServer";
import { language } from "../languageProvider/language/language";

// ============================================================================
// IVA / volantini B2B
// ----------------------------------------------------------------------------
// Ogni prodotto dichiara nel publication.json la propria aliquota (`iva`) e se
// il prezzo esposto e' imponibile o ivato (`priceType`: "ivaExcluded" oppure
// assente = IVA inclusa, comportamento storico). Le due cose sono per riga:
// un carrello puo' mescolare prezzi netti e prezzi ivati, quindi imponibile e
// IVA si calcolano riga per riga aggiungendo l'IVA ai netti e SCORPORANDOLA
// dagli ivati, e i due risultati confluiscono negli stessi totali.
//
// Cosa si mostra e' governato da due flag di config (assenti = false, cosi' i
// volantini gia' pubblicati non cambiano):
//   - show_vat_percentage -> l'aliquota compare su ogni riga del carrello e
//     del riepilogo prodotti del checkout;
//   - show_vat_breakdown  -> il blocco totali si splitta in imponibile + IVA;
//     se false si mostra solo il Totale, che resta comunque l'importo ivato
//     realmente da pagare.
// ============================================================================

export const PRICE_TYPE_VAT_EXCLUDED = "ivaExcluded";

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Spazio unificatore: tiene insieme "IVA Esclusa" e "IVA 10%" senza ricorrere
// a white-space: nowrap, che alzerebbe la larghezza minima del contenitore e
// farebbe sfondare la riga del carrello (il cestino usciva dalla card).
const NBSP = "\u00A0";
const keepTogether = (text) => String(text || "").replace(/ /g, NBSP);

const t = (key) => {
  // Niente import di defaultLanguage da helper/utility: creerebbe un ciclo
  // (utility -> ManagerFactory -> Default.manager -> vat).
  const dict = language[serverResponse.config.language] || {};
  return dict[key] || "";
};

// --- Flag di config -------------------------------------------------------

export const showVatPercentage = () =>
  serverResponse.config.show_vat_percentage === true;

export const showVatBreakdown = () =>
  serverResponse.config.show_vat_breakdown === true;

// Mostra, accanto al prezzo esposto, il suo corrispettivo nell'altro regime:
// un prezzo netto affianca l'ivato, un prezzo ivato affianca l'imponibile.
export const showPriceWithAndWithoutVat = () =>
  serverResponse.config.show_price_with_and_without_vat === true;

// --- Dati del singolo prodotto -------------------------------------------

// I carrelli gia' salvati in localStorage e le liste rilette da
// grocery-list/read possono non avere i campi iva/priceType: si rileggono dal
// publication.json a partire dall'item_id.
const getSourceProduct = (item = {}) => {
  const id =
    item.item_id !== undefined && item.item_id !== null ? item.item_id : item.id;
  return (serverResponse.interactivities || {})[id] || null;
};

// Aliquota del prodotto in percentuale (es. 22). 0 se non dichiarata.
export const getVatRate = (item = {}) => {
  let iva = item.iva;
  if (iva === undefined || iva === null || iva === "") {
    const source = getSourceProduct(item);
    iva = source ? source.iva : null;
  }
  const rate = parseFloat(iva);
  return isNaN(rate) ? 0 : rate;
};

// true se il prezzo di questa riga e' imponibile (IVA da aggiungere).
export const isVatExcludedItem = (item = {}) => {
  let priceType = item.priceType;
  if (priceType === undefined || priceType === null) {
    const source = getSourceProduct(item);
    priceType = source ? source.priceType : null;
  }
  return priceType === PRICE_TYPE_VAT_EXCLUDED;
};

// Il volantino espone almeno un prezzo imponibile.
export const isVatExcludedFlyer = () => {
  try {
    return Object.values(serverResponse.interactivities || {}).some(
      (p) => p && p.priceType === PRICE_TYPE_VAT_EXCLUDED
    );
  } catch {
    return false;
  }
};

// Aliquota formattata per la UI: "10" -> "10", "22.0" -> "22", 4.5 -> "4,5".
export const formatVatRate = (rate) => {
  const n = parseFloat(rate) || 0;
  return String(Math.round(n * 100) / 100).replace(".", ",");
};

/**
 * Diciture IVA della riga prodotto, gia' pronte da stampare in sequenza.
 * - prezzo imponibile -> "IVA esclusa";
 * - con show_vat_percentage si aggiunge l'aliquota ("IVA 10%") e, sui prezzi
 *   ivati, la dicitura "IVA inclusa": in un carrello misto deve restare
 *   evidente a quale regime appartiene ogni riga.
 * Ritorna [] quando non c'e' nulla da mostrare (volantino B2C senza flag).
 */
export const getItemVatLabels = (item = {}) => {
  if (item && item.no_price) return [];
  const excluded = isVatExcludedItem(item);
  const withPercentage = showVatPercentage();
  const labels = [];

  if (excluded) {
    labels.push(t("iva_esclusa"));
  } else if (withPercentage) {
    labels.push(t("iva_inclusa"));
  }

  if (withPercentage) {
    const rate = getVatRate(item);
    if (rate > 0) labels.push(`${t("iva")} ${formatVatRate(rate)}%`);
  }

  // Ogni dicitura resta intera, ma le diciture possono andare a capo tra loro.
  return labels.filter(Boolean).map(keepTogether);
};

// Importo a due decimali per la UI.
const formatAmount = (n) => (Math.round((n + Number.EPSILON) * 100) / 100).toFixed(2);

// I prezzi arrivano come stringhe di formato eterogeneo ("€ 122,00",
// "122.00", "1.234,50 €"): qui si estrae il numero. L'ultimo separatore
// decimale (virgola o punto) e' quello vero, gli altri sono di migliaia.
export const parseAmount = (value) => {
  if (typeof value === "number") return isNaN(value) ? 0 : value;
  if (!value) return 0;
  const raw = String(value).replace(/[^\d.,-]/g, "");
  if (!raw) return 0;
  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");
  const decimalSep = lastComma > lastDot ? "," : lastDot > -1 ? "." : "";
  let normalized = raw;
  if (decimalSep) {
    const thousandSep = decimalSep === "," ? "." : ",";
    normalized = raw.split(thousandSep).join("").replace(decimalSep, ".");
  }
  const n = parseFloat(normalized);
  return isNaN(n) ? 0 : n;
};

// IVA da AGGIUNGERE a un imponibile.
export const getVatAmount = (net, rate) =>
  round2((parseFloat(net) || 0) * ((parseFloat(rate) || 0) / 100));

// IVA gia' CONTENUTA in un importo ivato (scorporo).
export const getVatIncluded = (gross, rate) => {
  const value = parseFloat(gross) || 0;
  const r = parseFloat(rate) || 0;
  if (!value || !r) return 0;
  return round2(value - value / (1 + r / 100));
};

/**
 * Totali del carrello.
 *
 * Ogni riga contribuisce a imponibile e IVA secondo il proprio priceType, con
 * arrotondamento al centesimo per riga (le aliquote possono essere miste:
 * applicarne una sola al subtotale farebbe ballare qualche centesimo).
 *
 * @param {Array}  list             righe carrello (currentPrice gia' calcolato)
 * @param {Object} options
 * @param {number} options.shipping spese di spedizione da get-info
 * @returns {{
 *   showBreakdown: boolean,  // il blocco totali va splittato
 *   showPercentage: boolean, // le righe mostrano l'aliquota
 *   hasNetItems: boolean,    // c'e' almeno un prezzo imponibile in carrello
 *   itemsSubtotal: number,   // somma dei prezzi COSI' COME DICHIARATI:
 *                            // soglia minimo d'ordine e payload d'ordine
 *   itemsTaxable: number,    // imponibile della merce
 *   itemsVat: number,        // IVA della merce
 *   itemsTotal: number,      // merce ivata
 *   shipping: number,        // spedizione, non ivata
 *   taxable: number,         // imponibile merce + spedizione (non ivata)
 *   vat: number,             // IVA totale
 *   total: number            // importo finale da pagare
 * }}
 */
export const getCartTotals = (list = [], options = {}) => {
  const shippingRaw = parseFloat(options.shipping) || 0;

  let itemsSubtotal = 0;
  let itemsTaxable = 0;
  let itemsVat = 0;
  let hasNetItems = false;

  (list || []).forEach((item) => {
    if (!item || item.no_price) return;
    const line = parseFloat(item.currentPrice) || 0;
    const rate = getVatRate(item);
    itemsSubtotal += line;

    if (isVatExcludedItem(item)) {
      // Prezzo imponibile: l'IVA si aggiunge.
      hasNetItems = true;
      const vat = getVatAmount(line, rate);
      itemsTaxable += line;
      itemsVat += vat;
    } else {
      // Prezzo ivato: imponibile e IVA si scorporano e confluiscono negli
      // stessi totali delle righe nette (carrelli misti).
      const vat = getVatIncluded(line, rate);
      itemsTaxable += line - vat;
      itemsVat += vat;
    }
  });

  itemsSubtotal = round2(itemsSubtotal);
  itemsTaxable = round2(itemsTaxable);
  itemsVat = round2(itemsVat);

  // La spedizione arriva da get-info gia' definitiva: non genera IVA, ne' da
  // aggiungere ne' da scorporare (stesso trattamento del contrassegno). Entra
  // quindi nel Totale a valore pieno e lascia la riga IVA a quella della merce.
  const shipping = round2(shippingRaw);

  const taxable = round2(itemsTaxable + shipping);
  const vat = itemsVat;

  return {
    showBreakdown: showVatBreakdown(),
    showPercentage: showVatPercentage(),
    hasNetItems,
    itemsSubtotal,
    itemsTaxable,
    itemsVat,
    itemsTotal: round2(itemsTaxable + itemsVat),
    shipping,
    taxable,
    vat,
    total: round2(taxable + vat),
  };
};

/**
 * Prezzo della riga nell'altro regime IVA, gia' formattato:
 * - riga a prezzo imponibile -> "122,00 € IVA Inclusa";
 * - riga a prezzo ivato      -> "90,91 € IVA Esclusa".
 * Serve al flag show_price_with_and_without_vat, che affianca al prezzo
 * esposto il suo corrispettivo senza toccare nessun totale.
 * Ritorna "" quando non c'e' niente di sensato da mostrare: flag spento,
 * riga senza prezzo, aliquota non dichiarata (i due importi coinciderebbero).
 *
 * @param {object} item prodotto (per aliquota e regime; anche solo item_id)
 * @param {number|string} [amount] importo esposto; default item.currentPrice
 */
export const getCounterpartPriceLabel = (item = {}, amount) => {
  if (!showPriceWithAndWithoutVat()) return "";
  if (item && item.no_price) return "";

  const value = parseAmount(amount !== undefined ? amount : item.currentPrice);
  if (!value) return "";

  const rate = getVatRate(item);
  if (!rate) return "";

  const excluded = isVatExcludedItem(item);
  const counterpart = excluded
    ? round2(value + getVatAmount(value, rate))
    : round2(value - getVatIncluded(value, rate));

  const label = excluded ? t("iva_inclusa") : t("iva_esclusa");
  return keepTogether(`${formatAmount(counterpart)} € ${label}`);
};
